import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled';

import FormTextField from './FormTextField';
import FormError from './FormError';
import FieldGroup from './FieldGroup';
import PasswordField from './PasswordField';
import { EmailConfirmationError } from '../types';
import messages from '../pages/login/components/LoginForm.messages';
import passwordContainerMessages from './PasswordContainer.messages';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/new';

import pageMessages from '../containers/EmailConfirmationFormPage.old.messages';

interface ChangeEmailFormProps {
  /** The email that the user is changing _to_. */
  email: string;
  /** The error (if any) that the backend returned. */
  error: EmailConfirmationError | null;
  /** Whether or not a request is in progress. */
  isLoading: boolean;
  /** Called upon the button being clicked. Used for tracking. */
  onSubmitButtonClick: () => void;
  /** Called after checking the validity of the entered details upon form submission. */
  onSubmit: (email: string, password: string) => void;
}

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${token('space.100')}px;
`;

type ErrorContentProps = {
  error: EmailConfirmationError;
};

const ErrorContent = ({ error }: ErrorContentProps) => {
  switch (error) {
    case 'invalid_password':
      return (
        <FormattedMessage
          id="recovery.error.change-email.password.incorrect"
          defaultMessage="The password you entered is incorrect. Please try again."
        />
      );

    case 'invalid_token':
      return (
        <FormattedMessage
          id="recovery.error.change-email.invalid_token"
          defaultMessage="Something went wrong. Please try again later."
        />
      );

    case 'unknown_error':
      return (
        <FormattedMessage
          id="recovery.error.change-email.unknown_error"
          defaultMessage="Something went wrong while attempting to change your email. Please try again."
        />
      );

    default:
      return (
        <FormattedMessage
          id="recovery.error.change-email.unknown"
          defaultMessage="Something went wrong while attempting to change your email. Please try again."
        />
      );
  }
};

const ChangeEmailForm = ({
  email,
  isLoading,
  error,
  onSubmit,
  onSubmitButtonClick,
}: ChangeEmailFormProps) => {
  const intl = useIntl();
  const [password, setPassword] = useState('');
  const [passwordFieldError, setPasswordFieldError] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Reset the error state to let the validation happen again.
    setPasswordFieldError(null);

    // Prevent submission/default behaviour. We return early in the cases of validation errors, but otherwise call the submit function
    e.preventDefault();

    if (isLoading) {
      return;
    }

    if (password.trim() === '') {
      setPasswordFieldError(intl.formatMessage(messages.passwordEmpty));
      return;
    }

    if (error === 'invalid_password') {
      setPasswordFieldError(intl.formatMessage(pageMessages.incorrectPassword));
      return;
    }

    onSubmit(email, password);
  };

  const handlePasswordChange = (e: React.FormEvent<HTMLInputElement>) => {
    setPasswordFieldError(null);
    setPassword(e.currentTarget.value);
  };

  return (
    <div data-testid="email-verification-page-idf-testid">
      {error && (
        <FormError>
          <ErrorContent error={error} />
        </FormError>
      )}
      <StyledForm onSubmit={handleSubmit} id="form-change-email" noValidate>
        <FieldGroup>
          <FormTextField
            id="email"
            name="email"
            type="email"
            autoComplete="off"
            placeholder={intl.formatMessage(messages.emailEmpty)}
            label={<FormattedMessage {...pageMessages.emailLabel} />}
            value={email}
            ref={emailInputRef}
            isDisabled={true}
            isRequired
          />
        </FieldGroup>
        <FieldGroup>
          <PasswordField
            name="password"
            autoComplete="current-password"
            id="password"
            testId="password" // used in pollinators
            placeholder={intl.formatMessage(messages.passwordEmpty)}
            label={<FormattedMessage {...passwordContainerMessages.passwordLabel} />}
            autoFocus={true}
            isInvalid={!!passwordFieldError}
            invalidMessage={passwordFieldError}
            onChange={handlePasswordChange}
            value={password}
            ref={passwordInputRef}
            isRequired
          />
        </FieldGroup>
        <Button
          appearance="primary"
          type="submit"
          testId="change-email-submit-button-idf-testid"
          onClick={onSubmitButtonClick}
          isLoading={isLoading}>
          <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
        </Button>
      </StyledForm>
    </div>
  );
};

export default ChangeEmailForm;
